/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/react
 * @description Renders the Tailwind-based React adapter configuration form.
 * This component is used inside adapter pages and dialogs to collect the
 * provider-specific credentials required for each infrastructure domain.
 */

"use client";

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { FormEvent, JSX } from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { cn } from "../lib/utils";

/**
 * Props accepted by the React adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property className Optional class name merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  className?: string;
  titleId?: string;
}

const providerOptionBaseClassName =
  "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none";

const tierBaseClassName =
  "mt-2 inline-flex rounded px-2 py-0.5 text-[0.5rem] font-bold uppercase";

const tierClassNames = {
  lancio: "bg-accent/10 text-accent",
  pronto: "bg-primary/10 text-primary",
  studio: "bg-muted text-muted-foreground",
} as const;

function getProviderOptionClassName(selected: boolean): string {
  return cn(
    providerOptionBaseClassName,
    selected
      ? "border-primary/30 bg-primary/10 shadow-sm"
      : "border-border bg-muted/20 hover:border-primary/20 hover:bg-accent/5 active:border-primary/30 active:bg-accent/10",
  );
}

function getTierClassName(tier: keyof typeof tierClassNames): string {
  return cn(tierBaseClassName, tierClassNames[tier]);
}

interface ProviderOptionField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  inputMode?: "text" | "numeric";
}

const PROVIDER_OPTION_FIELDS: Partial<
  Record<AdapterDomain, Record<string, ProviderOptionField[]>>
> = {
  db: {
    convex: [
      {
        key: "deployment",
        label: "Deployment",
        description: "Convex deployment or project slug.",
        placeholder: "team:project",
      },
    ],
    neon: [
      {
        key: "database",
        label: "Database",
        description: "Database name or branch label for downstream wiring.",
        placeholder: "main",
      },
    ],
    cloudflareKV: [
      {
        key: "namespace",
        label: "KV Namespace",
        description: "Worker binding name used by the downstream KV adapter.",
        placeholder: "ADAPTERS_KV",
      },
      {
        key: "defaultTtlSeconds",
        label: "Default TTL Seconds",
        description: "Optional TTL for cache/session style records.",
        placeholder: "300",
        inputMode: "numeric",
      },
    ],
    memory: [
      {
        key: "seed",
        label: "Fixture Seed",
        description: "Optional deterministic seed for local test data.",
        placeholder: "demo",
      },
    ],
  },
};

const LAUNCH_DB_PROVIDER_IDS = new Set([
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
]);

function getSelectableProviders(domain: AdapterDomain) {
  const providers = getProvidersForDomain(domain);
  if (domain !== "db") {
    return providers;
  }

  return providers.filter((entry) => {
    if (entry.id === "localStorage") {
      return true;
    }
    return LAUNCH_DB_PROVIDER_IDS.has(entry.id);
  });
}

function getOptionFields(
  domain: AdapterDomain,
  provider: string,
): ProviderOptionField[] {
  return PROVIDER_OPTION_FIELDS[domain]?.[provider] ?? [];
}

function stringifyOptionValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

function coerceOptionValue(field: ProviderOptionField, value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  if (field.inputMode !== "numeric") {
    return trimmed;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
}

function validateOptionValues(
  domain: AdapterDomain,
  provider: string,
  optionValues: Record<string, string>,
): string | null {
  // Domain-specific checks first so they produce the most targeted message.
  if (domain === "db" && provider === "cloudflareKV") {
    if (!optionValues.namespace?.trim()) {
      return "KV namespace is required";
    }

    const ttlValue = optionValues.defaultTtlSeconds?.trim();
    if (ttlValue) {
      const ttlSeconds = Number(ttlValue);
      if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
        return "Default TTL seconds must be a positive integer";
      }
    }
  }

  // Generic numeric-field guard: any provider-option field declared with
  // inputMode "numeric" must parse as a finite number when set. Without this
  // check, coerceOptionValue silently returns the raw string for non-numeric
  // input (e.g. "abc"), which then ships as part of config.options.
  for (const field of getOptionFields(domain, provider)) {
    if (field.inputMode !== "numeric") continue;
    const raw = optionValues[field.key]?.trim();
    if (!raw) continue;
    if (!Number.isFinite(Number(raw))) {
      return `${field.label} must be a number`;
    }
  }

  return null;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with the React variant
 * @example
 * ```tsx
 * <AdapterConfigForm
 *   domain="ai"
 *   onSave={async (config) => saveConfig('ai', config)}
 *   onCancel={() => setOpen(false)}
 * />
 * ```
 */
export function AdapterConfigForm({
  domain,
  initialConfig,
  onSave,
  onCancel,
  className,
  titleId,
}: AdapterConfigFormProps): JSX.Element {
  const providers = useMemo(() => getSelectableProviders(domain), [domain]);
  const initialProvider =
    providers.find((entry) => entry.id === initialConfig?.provider)?.id ??
    providers[0]?.id ??
    "";
  const [provider, setProvider] = useState(initialProvider);
  const [apiKey, setApiKey] = useState(initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = useState(initialConfig?.baseUrl ?? "");
  const [optionValues, setOptionValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
        key,
        stringifyOptionValue(value),
      ]),
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providerGroupLabelId = useId();

  const selectedMeta = providers.find((entry) => entry.id === provider);
  const needsApiKey = selectedMeta?.requiresApiKey ?? false;
  const needsBaseUrl = selectedMeta?.requiresBaseUrl ?? false;
  const optionFields = getOptionFields(domain, provider);

  useEffect(() => {
    if (!needsApiKey && apiKey) {
      setApiKey("");
    }
    if (!needsBaseUrl && baseUrl) {
      setBaseUrl("");
    }
  }, [apiKey, baseUrl, needsApiKey, needsBaseUrl]);

  // Resynchronise form state when the caller swaps `domain` or `initialConfig`
  // on a mounted form. Without this, re-rendering from db→ai keeps the prior
  // db provider in state and `handleSubmit` would persist it for the new
  // domain. Also guards against a current provider that is no longer in the
  // selectable list for the new domain.
  useEffect(() => {
    const candidate = initialConfig?.provider;
    const nextProvider =
      providers.find((entry) => entry.id === candidate)?.id ??
      providers[0]?.id ??
      "";
    setProvider(nextProvider);
    setApiKey(initialConfig?.apiKey ?? "");
    setBaseUrl(initialConfig?.baseUrl ?? "");
    setOptionValues(
      Object.fromEntries(
        Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
          key,
          stringifyOptionValue(value),
        ]),
      ),
    );
    setError(null);
  }, [providers, initialConfig]);

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider) {
      setError("Select a provider");
      return;
    }

    // Defensive: reject providers no longer selectable for the current domain.
    // This is the second guard against a stale provider surviving a domain
    // change between render and submit.
    if (!providers.some((entry) => entry.id === provider)) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey && !apiKey.trim()) {
      setError("API key is required");
      return;
    }

    if (needsBaseUrl && !baseUrl.trim()) {
      setError("Base URL is required");
      return;
    }

    const optionError = validateOptionValues(domain, provider, optionValues);
    if (optionError) {
      setError(optionError);
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider };
      if (needsApiKey && apiKey.trim()) {
        config.apiKey = apiKey.trim();
      }
      if (needsBaseUrl && baseUrl.trim()) {
        config.baseUrl = baseUrl.trim();
      }
      const options = Object.fromEntries(
        optionFields
          .map((field) => [
            field.key,
            coerceOptionValue(field, optionValues[field.key] ?? ""),
          ])
          .filter(([, value]) => value !== ""),
      );
      if (Object.keys(options).length > 0) {
        config.options = options;
      }
      await onSave(config);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "space-y-6 rounded-lg border border-border bg-card p-6 shadow-sm",
        className,
      )}
      noValidate
    >
      <div>
        <h3 id={titleId} className="text-lg font-bold text-foreground">
          Configure {DOMAIN_LABELS[domain]}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Select and configure your preferred provider.
        </p>
      </div>

      {error && (
        <div
          className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error}
        </div>
      )}

      <fieldset className="space-y-3">
        <legend
          id={providerGroupLabelId}
          className="block text-xs font-semibold uppercase text-muted-foreground"
        >
          Provider
        </legend>
        <div
          className="grid gap-2 sm:grid-cols-2"
          role="radiogroup"
          aria-labelledby={providerGroupLabelId}
        >
          {providers.map((entry) => (
            <label
              key={entry.id}
              className={getProviderOptionClassName(provider === entry.id)}
            >
              <input
                type="radio"
                name={`adapter-${domain}-provider`}
                value={entry.id}
                checked={provider === entry.id}
                onChange={() => setProvider(entry.id)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <div className="flex-1">
                <span className="block text-sm font-bold text-foreground">
                  {entry.name}
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                  {entry.description}
                </span>
                <span className={getTierClassName(entry.tier)}>
                  {entry.tier}
                </span>
              </div>
            </label>
          ))}
        </div>
      </fieldset>

      {needsApiKey && (
        <div className="space-y-1.5">
          <label
            htmlFor={`adapter-${domain}-apiKey`}
            className="block text-xs font-semibold uppercase text-muted-foreground"
          >
            API Key
          </label>
          <input
            id={`adapter-${domain}-apiKey`}
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={`Enter ${selectedMeta?.name ?? provider} API key`}
            autoComplete="off"
            className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          />
        </div>
      )}

      {needsBaseUrl && (
        <div className="space-y-1.5">
          <label
            htmlFor={`adapter-${domain}-url`}
            className="block text-xs font-semibold uppercase text-muted-foreground"
          >
            Base URL
          </label>
          <input
            id={`adapter-${domain}-url`}
            type="url"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            placeholder="https://..."
            className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          />
        </div>
      )}

      {optionFields.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {optionFields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <label
                htmlFor={`adapter-${domain}-${field.key}`}
                className="block text-xs font-semibold uppercase text-muted-foreground"
              >
                {field.label}
              </label>
              <input
                id={`adapter-${domain}-${field.key}`}
                type="text"
                inputMode={field.inputMode}
                value={optionValues[field.key] ?? ""}
                onChange={(event) =>
                  setOptionValues((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
                placeholder={field.placeholder}
                aria-describedby={`adapter-${domain}-${field.key}-hint`}
                className="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
              />
              <p
                id={`adapter-${domain}-${field.key}-hint`}
                className="text-xs text-muted-foreground"
              >
                {field.description}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground active:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSaving || !provider}
          className="inline-flex items-center justify-center rounded-md border border-primary/30 bg-primary px-8 py-3 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 active:bg-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none"
        >
          {isSaving ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
